/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, createContext, PropsWithChildren } from 'react';
import { Drawer } from '@one-thd/sui-atomic-components';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { IDrawerBasicProps } from '../models/drawer';
import { useAddress } from '../hooks/use-address';
import { usePayments } from '../hooks/use-payments';

export interface IDrawerContextValues {
  isOpened: boolean;
  openDrawer: <P extends IDrawerBasicProps>(
    ContentComponent: React.FC<PropsWithChildren<P>>,
    componentProps: PropsWithChildren<P>,
    drawerProps?: IDrawerProps,
  ) => void;
  hideDrawer: () => void;
  setComponentProps: (componentProps: PropsWithChildren<any>) => void;
}

// Props that can be added to the stencil drawer
export interface IDrawerProps {
  children?: React.ReactNode;
  DrawerRootProps?: Record<string, any>;
  open?: boolean;
  onClose?: () => void;
  position?: 'left' | 'right';
  fixed?: boolean;
}

interface IDrawerState {
  isOpened: boolean;
  ContentComponent: React.FC<PropsWithChildren<any>> | null;
  componentProps: PropsWithChildren<any>;
  drawerProps: IDrawerProps;
}

interface IDrawerProviderProps {
  children: React.ReactNode;
}

const DrawerContext = createContext<IDrawerContextValues | null>(null);

const DrawerProvider = ({ children }: IDrawerProviderProps) => {
  const initialState = {
    ContentComponent: null,
    componentProps: {},
    drawerProps: {},
    isOpened: false,
  };
  const { setSelectedAddress } = useAddress();
  const { setSelectedPaymentMethod } = usePayments();
  const [drawerState, setDrawerState] = useState<IDrawerState>(initialState);

  /**
   * openDrawer function.
   *
   * @param {React.FC} ContentComponent - a drawer content component
   * @param {any} [componentProps={}] - a drawer content component (ContentComponent) props
   * @param {IDrawerProps} [drawerProps={}] - a stencil drawer props
   *
   * @example
   *     const BasicDrawerContent = ({title}: string) => <p>This is a basic drawer's content. {title}</p>;
   *     // Update  stencil drawer props
   *     const drawerProps = { position: 'left' };
   *
   *     openDrawer(BasicDrawerContent, { title: 'Drawer Title' }, drawerProps);
   */
  function openDrawer<P extends IDrawerBasicProps>(
    ContentComponent: React.FC<PropsWithChildren<P>>,
    componentProps: PropsWithChildren<P>,

    drawerProps: IDrawerProps = {},
  ) {
    setDrawerState({
      ContentComponent,
      componentProps,
      drawerProps,
      isOpened: true,
    });
  }

  /**
   * hideDrawer function for hiding (closing) opened drawer
   *
   */
  const hideDrawer = () => {
    setSelectedAddress(null);
    setSelectedPaymentMethod(undefined);
    setDrawerState(({ drawerProps }) => ({
      ContentComponent: null,
      componentProps: {},
      drawerProps: {
        position: drawerProps.position,
      },
      isOpened: false,
      viewsOpened: [],
    }));
  };

  function setComponentProps<P extends IDrawerBasicProps>(
    componentProps: PropsWithChildren<P>,
  ) {
    setDrawerState((prevDrawerState) => ({
      ...prevDrawerState,
      componentProps: { ...prevDrawerState.componentProps, ...componentProps },
    }));
  }

  return (
    <DrawerContext.Provider
      value={{
        openDrawer,
        hideDrawer,
        isOpened: drawerState.isOpened,
        setComponentProps,
      }}
    >
      <Drawer
        open={drawerState.isOpened}
        onClose={hideDrawer}
        {...drawerState.drawerProps}
      >
        {drawerState.ContentComponent && (
          <QueryProvider cacheKey="drawerContext">
            <drawerState.ContentComponent
              hideDrawer={hideDrawer}
              {...drawerState.componentProps}
            />
          </QueryProvider>
        )}
      </Drawer>
      {children}
    </DrawerContext.Provider>
  );
};

export { DrawerProvider, DrawerContext };
